import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket as TicketIcon } from "lucide-react";
import api from "../lib/api";
import { SectionTitle, Spinner, EmptyState, NeonButton, Pill } from "../components/Neon";
import { fmtDateTime } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const TicketRow = ({ tk }) => (
  <div className="neon-flow" data-testid={`ticket-${tk.id}`}>
    <div className="neon-inner p-0 overflow-hidden flex items-center gap-4">
      <img src={tk.hero_image} alt={tk.competition_title} className="h-20 w-28 object-cover" loading="lazy" />
      <div className="flex-1 py-3 pr-4">
        <p className="font-display uppercase text-lg text-white leading-tight">{tk.competition_title}</p>
        <p className="text-xs text-[var(--text-muted)]">{tk.prize_title} · {fmtDateTime(tk.created_at)}</p>
        <div className="mt-1 flex items-center gap-2">
          <span className="font-display text-xl glow-cyan">#{tk.ticket_number}</span>
          {tk.is_free && <Pill color="green">Free</Pill>}
          {tk.instant_win && <Pill color="pink">Instant Win</Pill>}
          {tk.winner && <Pill color="gold">Winner</Pill>}
        </div>
      </div>
    </div>
  </div>
);

export default function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/tickets").then(({ data }) => setTickets(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const active = tickets.filter((t) => t.status === "active" || !t.status);
  const past = tickets.filter((t) => t.status && t.status !== "active" && !t.winner);
  const winners = tickets.filter((t) => t.winner);

  const List = ({ items }) => items.length === 0
    ? <div className="mt-6"><EmptyState title="No tickets here yet" subtitle="Enter a competition to get started." action={<NeonButton as={Link} to="/competitions" variant="pink">Browse Competitions</NeonButton>} /></div>
    : <div className="mt-6 grid sm:grid-cols-2 gap-4">{items.map((tk) => <TicketRow key={tk.id} tk={tk} />)}</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
      <SectionTitle accent="white" sub="All your competition entries in one place."><TicketIcon className="inline h-7 w-7 mr-2 text-[var(--neon-pink)]" />My Tickets</SectionTitle>
      {loading ? <Spinner /> : (
        <Tabs defaultValue="active" className="mt-6" data-testid="my-tickets-list">
          <TabsList className="bg-[var(--surface)] border border-white/10">
            <TabsTrigger value="active" data-testid="tab-active-tickets">Active ({active.length})</TabsTrigger>
            <TabsTrigger value="winners" data-testid="tab-winner-tickets">Winners ({winners.length})</TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past-tickets">Past ({past.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="active"><List items={active} /></TabsContent>
          <TabsContent value="winners"><List items={winners} /></TabsContent>
          <TabsContent value="past"><List items={past} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
